// CSRFトークン取得
const getCsrfToken = (): string => {
  return document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") || "";
};

interface ApiRequestInit extends RequestInit {
  data?: unknown;
}

// 内部で使う共通フェッチ関数
const fetchClient = async (endpoint: string, { data, headers, ...customConfig }: ApiRequestInit = {}) => {
  const config: RequestInit = {
    ...customConfig,
    headers: {
      "Content-Type": "application/json",
      "Accept": "application/json", // ← これを追加（重要）
      "X-CSRF-Token": getCsrfToken(),
      ...headers,
    },
  };

  if (data) {
    config.body = JSON.stringify(data);
  }

  const response = await fetch(endpoint, config);

  // 401 Unauthorized (未ログイン)
  if (response.status === 401) {
    if (window.location.pathname !== '/login') {
      window.location.href = '/login';
    }
    throw new Error("Unauthorized");
  }

  // 422 Unprocessable Entity (セッション切れ等のカスタム処理)
  if (response.status === 422) {
    try {
      const clone = response.clone();
      const body = await clone.json();
      if (body.error === 'Session expired') {
        alert(body.message || "セッションが切れました。再ログインしてください。");
        if (window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
        throw new Error("Session expired");
      }
    } catch (e) {
      // JSONパースエラー等は無視して後続処理へ
    }
  }

  // エラーレスポンスの場合は例外を投げる（React Queryでキャッチするため）
  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    
    // 1. 明示的なメッセージがある場合 (message または error)
    let errorMessage = errorBody.message || errorBody.error;

    // 2. Railsのバリデーションエラーの場合 (例: { is_admin: ["..."], email: ["..."] })
    if (!errorMessage && typeof errorBody === "object") {
      // オブジェクトの値をすべて配列として取り出し、結合する
      const messages = Object.values(errorBody).flat();
      if (messages.length > 0) {
        errorMessage = messages.join("\n");
      }
    }

    throw new Error(errorMessage || `API Error: ${response.status}`);
  }

  return response;
};

export const apiClient = {
  get: async <T>(endpoint: string, config?: ApiRequestInit): Promise<T> => {
    const response = await fetchClient(endpoint, { ...config, method: "GET" });
    return response.json();
  },

  post: async <T>(endpoint: string, data?: unknown, config?: ApiRequestInit): Promise<T> => {
    const response = await fetchClient(endpoint, { ...config, method: "POST", data });
    return response.json();
  },

  put: async <T>(endpoint: string, data?: unknown, config?: ApiRequestInit): Promise<T> => {
    const response = await fetchClient(endpoint, { ...config, method: "PUT", data });
    return response.json();
  },

  patch: async <T>(endpoint: string, data?: unknown, config?: ApiRequestInit): Promise<T> => {
    const response = await fetchClient(endpoint, { ...config, method: "PATCH", data });
    const text = await response.text();
    return text ? JSON.parse(text) : ({} as T);
  },

  delete: async <T>(endpoint: string, config?: ApiRequestInit): Promise<T> => {
    const response = await fetchClient(endpoint, { ...config, method: "DELETE" });
    return response.status === 204 ? ({} as T) : response.json();
  },

  download: async (endpoint: string, data?: unknown, config?: ApiRequestInit): Promise<Blob> => {
    const response = await fetchClient(endpoint, { ...config, method: "POST", data });
    return response.blob(); // JSONではなく blob() を返す
  },
};